import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, RefreshCw, ChevronLeft, Plus, Trash2, AlertTriangle, Printer, Check, Clock, X, Receipt, Wallet, Syringe,
} from 'lucide-react';
import { invoiceAPI, medicalServiceAPI, clinicalSettingsAPI, brandingAPI } from '../../services/api';
import { useToast, useConfirm } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import { clinicNow, clinicToday, formatClinicDate, formatClinicTime, toClinicDate } from '../../utils/clinicTime';
import DatePicker from '../DatePicker';
import { formatPatientAge } from '../../utils/age';
import { formatVisitToken } from '../TokenPrintModal';
import { PAYMENT_MODE_LABEL } from '../../constants/payments';
import { toPaisa, formatRs, formatPaisa, formatSigned } from '../../utils/money';
import CashTenderPanel, { emptyTender } from './CashTenderPanel';
import { receiptDoctorName } from './InvoiceReceipt';
import InvoiceReceiptModal from './InvoiceReceiptModal';
import { useCashDrawer, isDrawerCancelled, openedBeforeToday } from '../cashDrawer/CashDrawerContext';
import CloseDrawerDialog from '../cashDrawer/CloseDrawerDialog';
import PrintableInvoice from './PrintableInvoice';
import {
  InvoiceDiscountModeToggle, ItemDiscountInput, ShortfallQuantityInput, AddServiceModal, CustomItemModal,
} from './InvoiceEditors';

// ============================================================================================
// The billing counter: one screen to bill a patient, collect the money and print the receipt.
//
// Opening a waiting patient shows the bill as a PREVIEW, built by the server from the prescription
// and saved nowhere, so looking at a patient locks nothing. The first change to a line saves it as
// a draft (the prescription locks then, as it always did), and Collect on an unsaved bill creates
// and pays it in one transaction. Every rule about what a bill may contain stays on the server.
// ============================================================================================

const COUNTER_PATH = '/invoice/counter';

/**
 * The patient or bill open at the counter, from the address: #/invoice/counter/rx/<id>, /bill/<id>,
 * or /course/<id> for a patient back for the next dose of an injection or drip course.
 */
export const parseCounterHash = (hash = window.location.hash) => {
  const m = String(hash || '').match(/^#?\/?invoice\/counter\/(rx|bill|course)\/([0-9a-fA-F-]{36})/);
  return m ? { kind: m[1], id: m[2] } : null;
};

export const counterHash = (kind, id) => `${COUNTER_PATH}/${kind}/${id}`;

const setHash = (path, { replace = false } = {}) => {
  const target = `#${path}`;
  if (window.location.hash === target) return;
  if (replace) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${target}`);
  else window.location.hash = path;
};

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// A prescription can wait days to be billed (the patient comes back for the medicines), so older
// ones stay reachable: This month, All (everything still waiting, whatever its date) and a custom
// range of any length.
const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7days', label: 'Last 7 days' },
  { key: 'month', label: 'This month' },
  { key: 'all', label: 'All' },
  { key: 'custom', label: 'Custom' },
];

// Both are date-only ISO strings parsed the same way, so the difference is whole days.
const rangeDays = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / 86400000);

// Clinic-zone days, so the list matches the server's idea of "today" whatever the browser's zone.
// All has no dates at all; a custom range with a date still missing has no range yet.
const rangeFor = (preset, custom) => {
  if (preset === 'all') return { all: true };
  const today = clinicNow();
  today.setHours(0, 0, 0, 0);
  const iso = toIso(today);
  if (preset === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { from: toIso(y), to: toIso(y) };
  }
  if (preset === '7days') {
    const s = new Date(today);
    s.setDate(s.getDate() - 6);
    return { from: toIso(s), to: iso };
  }
  if (preset === 'month') {
    const s = new Date(today);
    s.setDate(1);
    return { from: toIso(s), to: iso };
  }
  if (preset === 'custom') {
    if (!custom?.from || !custom?.to) return null;
    return { from: custom.from, to: custom.to };
  }
  return { from: iso, to: iso };
};

const startedAgo = (createdAt) => {
  const d = toClinicDate(createdAt);
  if (!d) return '';
  const mins = Math.max(0, Math.round((clinicNow() - d) / 60000));
  if (mins < 1) return 'Started just now';
  if (mins < 60) return `Started ${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `Started ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `Started ${days} day${days === 1 ? '' : 's'} ago`;
};

const titleCase = (s) => (s ? String(s).charAt(0) + String(s).slice(1).toLowerCase() : '');

const DEFAULT_SETTINGS = { roundingRule: 'DOWN_1', autoPrint: true, paper: 'SLIP_80MM' };

const readSettings = (s) => ({
  roundingRule: s?.billingRoundingRule || 'DOWN_1',
  autoPrint: s?.billingReceiptAutoPrint !== false,
  paper: s?.billingReceiptPaper === 'A4' ? 'A4' : 'SLIP_80MM',
});

const DISCARD_REASONS = ['Patient did not buy', 'Buying the medicines outside', 'Duplicate bill'];

// ============================================================================================
/**
 * What a bill comes to under one payment mode. Cash is rounded down further than the rest, because
 * notes cannot settle small change; the backend computes both figures and sends them with the bill,
 * so this only has to choose between them rather than re-implement the rule. A bill written before
 * cash rounding existed has no cash figure, and then the ordinary total stands for every mode.
 */
export const totalForMode = (bill, mode) => {
  if (!bill) return 0;
  const cash = mode === 'CASH' && bill.cashGrandTotal != null ? bill.cashGrandTotal : null;
  return Number(cash != null ? cash : bill.grandTotal) || 0;
};

const BillingCounter = ({ can }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const { withDrawer, drawer, loaded: drawerLoaded, active: drawerActive, askToOpen, refresh: refreshDrawer } = useCashDrawer();
  const allowed = (action) => (typeof can === 'function' ? can(action) : true);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [branding, setBranding] = useState(null);
  const [services, setServices] = useState([]);

  const [preset, setPreset] = useState('today');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [query, setQuery] = useState('');
  const [list, setList] = useState({ waiting: [], dosesDue: [], inProgress: [] });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [selection, setSelection] = useState(() => parseCounterHash());
  const [bill, setBill] = useState(null); // an invoice; an unsaved preview has id null
  const [shortfalls, setShortfalls] = useState([]);
  const [billLoading, setBillLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tender, setTender] = useState(emptyTender);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null);
  const [paidNow, setPaidNow] = useState(false);
  const [banner, setBanner] = useState(null); // { tone: 'warn' | 'error', text }
  const [showAddService, setShowAddService] = useState(false);
  const [showSyringe, setShowSyringe] = useState(false);
  const [showCustomItem, setShowCustomItem] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [printA4, setPrintA4] = useState(null); // the bill printing on A4, until the print dialog closes
  const [receipt, setReceipt] = useState(null); // { invoice, afterPayment } while the receipt dialog is open

  const searchRef = useRef(null);
  const nextPatientRef = useRef(null);
  const collectRef = useRef(null);
  const askedForDrawerRef = useRef(false);
  const askedToCloseRef = useRef(false);
  const [closingDrawer, setClosingDrawer] = useState(null); // an earlier day's drawer being closed
  const [, setDayTick] = useState(0);
  const billRef = useRef(null);
  const selectionRef = useRef(selection);
  const loadedKeyRef = useRef(null);
  billRef.current = bill;
  selectionRef.current = selection;

  // ---------------------------------------------------------------- settings, branding, services
  useEffect(() => {
    let live = true;
    const loadSettings = () => clinicalSettingsAPI.get()
      .then((s) => { if (live) setSettings(readSettings(s)); })
      .catch(() => {});
    loadSettings();
    brandingAPI.get().then((b) => { if (live) setBranding(b); }).catch(() => {});
    medicalServiceAPI.getAll().then((s) => { if (live) setServices(Array.isArray(s) ? s : []); }).catch(() => {});
    window.addEventListener('clinical-settings:changed', loadSettings);
    return () => { live = false; window.removeEventListener('clinical-settings:changed', loadSettings); };
  }, []);

  // ---------------------------------------------------------------- the list
  const loadList = async ({ quiet = false } = {}) => {
    if (!quiet) setListLoading(true);
    try {
      const range = rangeFor(preset, custom);
      if (!range) {
        // Custom with a date still to pick: nothing to list yet, but bills in progress still show.
        setList((l) => ({ ...l, waiting: [] }));
        setListError(null);
        return;
      }
      if (!range.all && rangeDays(range.from, range.to) < 0) {
        setListError('The end date is before the start date.');
        return;
      }
      const data = await invoiceAPI.counter(range);
      setList({
        waiting: data?.waiting || [],
        dosesDue: data?.dosesDue || [],
        inProgress: data?.inProgress || [],
      });
      setListError(null);
    } catch (err) {
      if (!quiet) setListError(err?.message || 'Could not load the counter');
    } finally {
      if (!quiet) setListLoading(false);
    }
  };

  // ---------------------------------------------------------------- selection and the bill
  const select = (kind, id, { keepTender = false } = {}) => {
    setBanner(null);
    setPaid(null);
    setPaidNow(false);
    if (!keepTender) setTender(emptyTender());
    setSelection({ kind, id });
    setHash(counterHash(kind, id));
  };

  const backToList = () => {
    loadedKeyRef.current = null;
    setSelection(null);
    setBill(null);
    setShortfalls([]);
    setPaid(null);
    setPaidNow(false);
    setBanner(null);
    setTender(emptyTender());
    setHash('/invoice');
    loadList({ quiet: true });
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  /** Loads what the selection points at. Never touches the tender, so typed cash survives a reload. */
  const loadBill = async (sel) => {
    if (!sel) return;
    setBillLoading(true);
    try {
      if (sel.kind === 'rx' || sel.kind === 'course') {
        // A course visit's bill is previewed the same way: built by the server, saved by nothing.
        const res = sel.kind === 'rx'
          ? await invoiceAPI.preview(sel.id)
          : await invoiceAPI.coursePreview(sel.id);
        setBill(res?.invoice || null);
        setShortfalls(res?.shortfalls || []);
        setPaid(null);
      } else {
        const inv = await invoiceAPI.getById(sel.id);
        if (inv?.status === 'PAID') {
          setBill(inv);
          setPaid(inv);
        } else if (inv?.status === 'DRAFT') {
          setBill(inv);
          setPaid(null);
        } else {
          showToast('error', `Bill ${inv?.invoiceNumber || ''} was discarded.`.replace('  ', ' '));
          backToList();
        }
      }
    } catch (err) {
      const handled = await handleRefusal(err);
      if (!handled) {
        showToast('error', err?.message || 'Could not open this bill');
        backToList();
      }
    } finally {
      setBillLoading(false);
    }
  };

  const reloadCurrent = () => {
    const sel = selectionRef.current;
    if (sel) return loadBill(sel);
    return Promise.resolve();
  };

  /**
   * The server's refusals that the counter can act on (CONTRACT: TOTAL_CHANGED, STOCK_SHORT,
   * BILL_EXISTS, NOT_BILLABLE, NOT_DRAFT). Returns true when handled. Typed cash and attached
   * screenshots are left alone in every case.
   */
  const handleRefusal = async (err) => {
    const data = err?.data || err?.body || {};
    switch (data.code) {
      case 'BILL_EXISTS':
        if (!data.invoiceId) return false;
        if (data.invoiceStatus === 'PAID') {
          loadedKeyRef.current = `bill:${data.invoiceId}`;
          setSelection({ kind: 'bill', id: data.invoiceId });
          setHash(counterHash('bill', data.invoiceId), { replace: true });
          try {
            const inv = await invoiceAPI.getById(data.invoiceId);
            setBill(inv);
            setPaid(inv);
            showToast('info', `This bill was already paid (${inv.invoiceNumber}).`);
          } catch {
            showToast('error', err.message);
          }
        } else {
          showToast('info', 'This bill was already started. Opening the saved bill.');
          loadedKeyRef.current = null;
          select('bill', data.invoiceId, { keepTender: true });
        }
        loadList({ quiet: true });
        return true;
      case 'NOT_BILLABLE':
        showToast('error', err.message || 'This prescription has nothing to bill.');
        backToList();
        return true;
      case 'TOTAL_CHANGED':
        setBanner({
          tone: 'warn',
          text: `The bill changed to ${formatRs(data.grandTotal)}. Tell the patient the new total, then collect.`,
        });
        await reloadCurrent();
        return true;
      case 'STOCK_SHORT':
        setBanner({ tone: 'error', text: err.message || 'Not enough stock to dispense this bill.' });
        await reloadCurrent();
        return true;
      case 'NOT_DRAFT':
        await reloadCurrent();
        return true;
      default:
        return false;
    }
  };

  // Functions change every render; effects reach the latest ones through this ref.
  const latest = useRef({});
  latest.current = { loadList, loadBill };

  useEffect(() => { latest.current.loadList(); }, [preset, custom.from, custom.to]);

  // Waiting patients arrive while the list is on screen: refresh it quietly every 30 seconds. While a
  // bill is open the list is not shown, and going back to it reloads it anyway.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible' && !selectionRef.current) latest.current.loadList({ quiet: true });
    }, 30000);
    return () => clearInterval(t);
  }, []);

  // Back and forward, and the sidebar re-click (which sets #/invoice).
  useEffect(() => {
    const onHash = () => setSelection(parseCounterHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!selection) {
      loadedKeyRef.current = null;
      setBill(null);
      setShortfalls([]);
      setPaid(null);
      return;
    }
    const key = `${selection.kind}:${selection.id}`;
    if (loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    latest.current.loadBill(selection);
  }, [selection]);

  // A saved draft re-checks its stock whenever its lines or quantities change. A preview arrives
  // with its shortfalls already worked out.
  const itemsKey = (bill?.items || []).map((i) => `${i.id}:${i.quantity}`).join(',');
  useEffect(() => {
    if (!bill?.id || bill.status !== 'DRAFT') return undefined;
    let live = true;
    invoiceAPI.stockCheck(bill.id)
      .then((r) => { if (live) setShortfalls(r?.shortfalls || []); })
      // A failed check must not block billing; the payment itself is still guarded.
      .catch(() => { if (live) setShortfalls([]); });
    return () => { live = false; };
  }, [bill?.id, bill?.status, itemsKey]);

  // After a payment with no receipt dialog, focus waits on Next patient. While the dialog is up focus
  // stays on its Print button, so an Enter meant for Print cannot reach Next patient behind it.
  useEffect(() => {
    if (paid && paidNow && !receipt && nextPatientRef.current) nextPatientRef.current.focus();
  }, [paid, paidNow, receipt]);

  // The A4 page is held on its own rather than read from the open bill: printing the receipt of a
  // payment also takes the counter back to the list, and the page has to stay until it has printed.
  // window.print() returns once the print dialog closes.
  useEffect(() => {
    if (!printA4) return undefined;
    const t = setTimeout(() => {
      window.print();
      setPrintA4(null);
    }, 50);
    return () => clearTimeout(t);
  }, [printA4]);

  // The counter takes money, so it needs a drawer to put it in. Arriving here with none open asks
  // for it once; closing that question leaves the counter waiting rather than half usable, so a
  // patient is never billed with the cash going nowhere.
  const needsDrawer = drawerActive && drawerLoaded && !drawer && allowed('FINALIZE_INVOICE');
  useEffect(() => {
    if (!needsDrawer || askedForDrawerRef.current) return;
    askedForDrawerRef.current = true;
    askToOpen('counter');
  }, [needsDrawer, askToOpen]);

  // A drawer still open from an earlier day is counted and closed before the new day's billing
  // starts (owner, 2026-10-02), so each day's cash is counted on its own. Arriving with one asks for
  // the count straight away; backing out leaves the counter held, as with no drawer. Once it is
  // closed, the question above asks for today's drawer.
  const drawerFromEarlierDay =
    drawerActive && drawerLoaded && openedBeforeToday(drawer) && allowed('FINALIZE_INVOICE');
  useEffect(() => {
    if (!drawerFromEarlierDay || askedToCloseRef.current) return;
    askedToCloseRef.current = true;
    setClosingDrawer(drawer);
  }, [drawerFromEarlierDay, drawer]);
  // The day can turn while the counter is open: look again every minute while a drawer is open.
  useEffect(() => {
    if (!drawer) return undefined;
    const t = setInterval(() => setDayTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, [drawer]);
  const closeDrawerDialog = closingDrawer && (
    <CloseDrawerDialog
      drawer={closingDrawer}
      // The closing slip stays up until Done; only then does the counter move on to today's drawer.
      onClose={() => {
        setClosingDrawer(null);
        refreshDrawer();
      }}
    />
  );

  // ---------------------------------------------------------------- edits
  const matchLine = (saved, item) => {
    const found = item.id
      ? (saved.items || []).find((i) => i.id === item.id)
      : (saved.items || []).find((i) => i.prescriptionMedicine?.id && i.prescriptionMedicine.id === item.prescriptionMedicine?.id);
    if (!found) throw new Error('That line is no longer on the bill.');
    return found;
  };

  /**
   * Saves a change. The first change to a preview saves the bill first (initiate), and the change is
   * then made to the matching saved line. Resolves true when it went through.
   */
  const applyEdit = async (change) => {
    if (editing) return false;
    setEditing(true);
    try {
      let current = billRef.current;
      if (!current) return false;
      if (!current.id) {
        current = current.treatmentCourseId
          ? await invoiceAPI.courseInitiate(current.treatmentCourseId)
          : await invoiceAPI.initiate(current.prescription.id);
        loadedKeyRef.current = `bill:${current.id}`;
        setBill(current);
        setSelection({ kind: 'bill', id: current.id });
        setHash(counterHash('bill', current.id), { replace: true });
        loadList({ quiet: true });
      }
      const updated = await change(current);
      if (updated) setBill(updated);
      return true;
    } catch (err) {
      const handled = await handleRefusal(err);
      if (!handled) showToast('error', err?.message || 'Could not save the change');
      return false;
    } finally {
      setEditing(false);
    }
  };

  const saveDiscount = (item, value) =>
    applyEdit((saved) => invoiceAPI.updateItemDiscount(saved.id, matchLine(saved, item).id, value));
  const saveQuantity = (item, quantity) =>
    applyEdit((saved) => invoiceAPI.updateItemQuantity(saved.id, matchLine(saved, item).id, quantity));
  const changeDiscountMode = (mode) =>
    applyEdit((saved) => invoiceAPI.updateDiscountMode(saved.id, mode));

  const addService = async (serviceId) => {
    const ok = await applyEdit((saved) => invoiceAPI.addExtraService(saved.id, serviceId));
    if (ok) { setShowAddService(false); showToast('success', 'Service added to the bill'); }
  };
  // Every medical supply, whether or not the prescription mentions it. A dressing, a cannula or a
  // pair of gloves gets used at the counter and the doctor never writes it down; what was actually
  // given belongs on the bill, and the Supplies Given report reads it back.
  // Keyed on the PRESCRIPTION, not the invoice: a bill being built is an unsaved preview whose id
  // is null, which is exactly when the counter wants to add a supply. Adding one is safe there too,
  // because applyEdit saves the draft first.
  const [supplies, setSupplies] = useState([]);
  const billPrescriptionId = bill?.prescription?.id || null;
  useEffect(() => {
    let cancelled = false;
    if (!billPrescriptionId) { setSupplies([]); return undefined; }
    invoiceAPI.supplyOptions(billPrescriptionId)
      .then((d) => { if (!cancelled) setSupplies(d?.supplies || []); })
      .catch(() => { if (!cancelled) setSupplies([]); });
    return () => { cancelled = true; };
  }, [billPrescriptionId]);

  const addSupply = async (medicineId) => {
    const ok = await applyEdit((saved) => invoiceAPI.addSupply(saved.id, medicineId, 1));
    if (ok) { setShowSyringe(false); showToast('success', 'Supply added to the bill'); }
  };

  const addCustomItem = async (item) => {
    const ok = await applyEdit((saved) => invoiceAPI.addCustomItem(saved.id, item));
    if (ok) { setShowCustomItem(false); showToast('success', 'Item added to the bill'); }
  };
  const removeLine = async (item) => {
    const yes = await confirm({
      title: 'Remove Item',
      message: `Remove "${item.name}" from the bill?`,
      confirmText: 'Remove',
      tone: 'danger',
    });
    if (!yes) return;
    const ok = await applyEdit((saved) => invoiceAPI.removeItem(saved.id, matchLine(saved, item).id));
    if (ok) showToast('success', 'Item removed');
  };

  // ---------------------------------------------------------------- collect
  // Once the receipt of a payment just taken is printed or skipped, the counter goes back to the list
  // for the next patient. Closing a reprint of a bill paid earlier leaves that bill open.
  const closeReceipt = () => {
    const afterPayment = receipt?.afterPayment;
    setReceipt(null);
    if (afterPayment) backToList();
  };

  const collect = async (payment, files) => {
    const current = billRef.current;
    if (!current || busy) return;
    setBusy(true);
    setBanner(null);
    try {
      // Cash rounds down further than the other modes, so which figure the patient was told
      // depends on how they are paying. Sending the wrong one is refused as TOTAL_CHANGED rather
      // than charged, but the point is to send the right one.
      const expectedTotal = totalForMode(current, payment?.paymentMode);
      const body = { expectedTotal, ...payment };
      // Cash goes into the cashier's drawer, which is opened first if it is not already.
      const result = await withDrawer(
        () => (current.id
          ? invoiceAPI.finalizeAndPay(current.id, body, files)
          : current.treatmentCourseId
            ? invoiceAPI.courseCheckout(current.treatmentCourseId, body, files)
            : invoiceAPI.checkout(current.prescription.id, body, files)),
        { cash: payment?.paymentMode === 'CASH' && expectedTotal > 0 },
      );
      loadedKeyRef.current = `bill:${result.id}`;
      setBill(result);
      setPaid(result);
      setPaidNow(true);
      setSelection({ kind: 'bill', id: result.id });
      setHash(counterHash('bill', result.id), { replace: true });
      setTender(emptyTender());
      showToast('success', `Bill ${result.invoiceNumber} paid`);
      loadList({ quiet: true });
      // Like a token: the receipt is shown first, and the cashier chooses to print it or skip. Either
      // way the counter then goes back to the list.
      if (settings.autoPrint && allowed('PRINT_INVOICE')) setReceipt({ invoice: result, afterPayment: true });
    } catch (err) {
      if (isDrawerCancelled(err)) return;
      const handled = await handleRefusal(err);
      if (!handled) setBanner({ tone: 'error', text: err?.message || 'The payment was not saved.' });
    } finally {
      setBusy(false);
    }
  };

  const discard = async (reason) => {
    const current = billRef.current;
    if (!current?.id) return;
    setDiscarding(true);
    try {
      await invoiceAPI.cancel(current.id, reason);
      showToast('success', `Bill ${current.invoiceNumber} discarded`);
      setShowDiscard(false);
      backToList();
    } catch (err) {
      showToast('error', err?.message || 'Could not discard the bill');
    } finally {
      setDiscarding(false);
    }
  };

  // ---------------------------------------------------------------- derived
  const q = query.trim().toLowerCase();
  const matches = (r) => {
    if (!q) return true;
    const token = String(formatVisitToken({ tokenNumber: r.tokenNumber, tokenPrefix: r.tokenPrefix }) || '').toLowerCase();
    return [r.patientName, r.mrn, r.phoneNumber, r.prescriptionNumber, r.invoiceNumber, token]
      .some((v) => v && String(v).toLowerCase().includes(q));
  };
  const waiting = list.waiting.filter(matches);
  const dosesDue = (list.dosesDue || []).filter(matches);
  const inProgress = list.inProgress.filter(matches);

  const shortByMedicine = useMemo(
    () => Object.fromEntries((shortfalls || []).map((s) => [s.medicineId, s])),
    [shortfalls],
  );

  const isPaid = bill?.status === 'PAID';
  const editable = !!bill && bill.status === 'DRAFT' && !busy;
  const items = [...(bill?.items || [])].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const gross = items.reduce((s, i) => s + toPaisa(i.unitPrice) * (Number(i.quantity) || 0), 0);
  const lineDiscounts = items.reduce((s, i) => s + toPaisa(i.discountAmount), 0);
  // What this bill comes to under the mode the cashier has picked. The backend computes both and
  // sends them with the bill, so the rule lives in one place rather than being re-implemented here.
  const payingCash = tender.mode === 'CASH';
  const shownTotal = payingCash && bill?.cashGrandTotal != null ? bill.cashGrandTotal : bill?.grandTotal;
  const shownRounding = payingCash && bill?.cashRoundingAdjustment != null
    ? bill.cashRoundingAdjustment
    : bill?.roundingAdjustment;
  const percentMode = (bill?.discountMode || 'PERCENT') === 'PERCENT';

  let blockedReason = null;
  if (billLoading) blockedReason = 'Loading the bill.';
  else if (editing) blockedReason = 'Saving the change.';
  else if (bill && items.length === 0) blockedReason = 'This bill has no items.';
  else if (shortfalls.length > 0) blockedReason = 'Not enough stock to dispense this bill. Reduce the quantity or remove the item first.';

  const patient = bill?.patient || {};
  const patientName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
  // A course visit's bill still names the prescription's visit, but the patient came back without a
  // token today, so the first day's token number would be the wrong one to show.
  const token = bill?.visit && !bill?.treatmentCourseId ? formatVisitToken(bill.visit) : '';
  const doctor = bill ? receiptDoctorName(bill) : '';

  // ---------------------------------------------------------------- render
  const listPane = (
    <div className="bg-white rounded-2xl shadow-xl">
      <div className="p-4 space-y-3 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            placeholder="Search name, MRN, phone, token or Rx"
            aria-label="Search the billing counter"
            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={preset === p.key}
              onClick={() => setPreset(p.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                preset === p.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            aria-label="Refresh the list"
            title="Refresh"
            onClick={() => loadList()}
            className="ml-auto p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 text-gray-500 ${listLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {preset === 'custom' && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="block text-[11px] font-semibold text-gray-500 mb-1">From</span>
              <DatePicker
                value={custom.from}
                onChange={(v) => setCustom((c) => ({ ...c, from: v || '' }))}
                max={custom.to || clinicToday()}
                placeholder="Start date"
              />
            </div>
            <div>
              <span className="block text-[11px] font-semibold text-gray-500 mb-1">To</span>
              <DatePicker
                value={custom.to}
                onChange={(v) => setCustom((c) => ({ ...c, to: v || '' }))}
                min={custom.from || undefined}
                max={clinicToday()}
                placeholder="End date"
              />
            </div>
          </div>
        )}
      </div>

      {listError && (
        <div className="m-4 rounded-lg bg-red-50 border-l-4 border-red-500 p-3 text-sm text-red-700">
          {listError}{' '}
          <button type="button" onClick={() => loadList()} className="font-semibold underline">Try again</button>
        </div>
      )}

      {inProgress.length > 0 && (
        <div className="py-2 border-b border-gray-100">
          <h3 className="px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-500">Bills in progress</h3>
          {inProgress.map((r) => {
            const active = selection?.kind === 'bill' && selection.id === r.invoiceId;
            const tok = formatVisitToken({ tokenNumber: r.tokenNumber, tokenPrefix: r.tokenPrefix });
            return (
              <button
                key={r.invoiceId}
                type="button"
                aria-current={active ? 'true' : undefined}
                onClick={() => select('bill', r.invoiceId)}
                className={`w-full text-left px-4 py-2.5 flex items-start justify-between gap-3 hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-emerald-50 ${
                  active ? 'bg-emerald-50 shadow-[inset_3px_0_0_rgb(var(--c-emerald-600))]' : ''
                }`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900 truncate">{r.patientName}</span>
                  <span className="block text-xs text-gray-500">{r.mrn}{tok ? ` · ${tok}` : ''}</span>
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    <Clock className="h-3 w-3" aria-hidden="true" /> {startedAgo(r.createdAt)}
                  </span>
                  {r.treatmentCourseId && (
                    <span className="mt-1 ml-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                      <Syringe className="h-3 w-3" aria-hidden="true" /> Today's dose
                    </span>
                  )}
                </span>
                <span className="text-sm font-semibold text-gray-700 tabular-nums whitespace-nowrap">{formatRs(r.grandTotal)}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Patients back for the next dose of an injection or drip given here one dose per visit. They
          have no new prescription: the bill is that visit's dose, with the fee paid last time. */}
      {dosesDue.length > 0 && (
        <div className="py-2 border-b border-gray-100">
          <h3 className="px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-500">
            Back for today's dose · {dosesDue.length}
          </h3>
          {dosesDue.map((r) => {
            const active = selection?.kind === 'course' && selection.id === r.courseId;
            return (
              <button
                key={r.courseId}
                type="button"
                aria-current={active ? 'true' : undefined}
                onClick={() => select('course', r.courseId)}
                className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-emerald-50 ${
                  active ? 'bg-emerald-50 shadow-[inset_3px_0_0_rgb(var(--c-emerald-600))]' : ''
                }`}
              >
                <span className="block text-sm font-semibold text-gray-900 truncate">{r.patientName}</span>
                <span className="block text-xs text-gray-500">
                  {r.mrn}{r.prescriptionNumber ? ` · ${r.prescriptionNumber}` : ''}
                </span>
                {(r.doses || []).map((d) => (
                  <span
                    key={`${d.medicineName}-${d.dose}`}
                    className="mt-1 mr-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                  >
                    <Syringe className="h-3 w-3" aria-hidden="true" /> {d.medicineName} · Dose {d.dose} of {d.of}
                  </span>
                ))}
              </button>
            );
          })}
        </div>
      )}

      <div className="py-2">
        <h3 className="px-4 py-1 text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Waiting to be billed · {waiting.length}
        </h3>
        {listLoading && list.waiting.length === 0 ? (
          <div className="px-4 py-2 space-y-2" aria-hidden="true">
            {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse" />)}
          </div>
        ) : waiting.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 text-center">
            {q ? `No patient matches "${query.trim()}".` : 'Nobody is waiting to be billed.'}
          </p>
        ) : (
          waiting.map((r) => {
            const active = selection?.kind === 'rx' && selection.id === r.prescriptionId;
            const tok = formatVisitToken({ tokenNumber: r.tokenNumber, tokenPrefix: r.tokenPrefix });
            return (
              <button
                key={r.prescriptionId}
                type="button"
                aria-current={active ? 'true' : undefined}
                onClick={() => select('rx', r.prescriptionId)}
                className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-emerald-50 ${
                  active ? 'bg-emerald-50 shadow-[inset_3px_0_0_rgb(var(--c-emerald-600))]' : ''
                }`}
              >
                <span className="block text-sm font-semibold text-gray-900 truncate">{r.patientName}</span>
                <span className="block text-xs text-gray-500">
                  {r.mrn}{tok ? ` · ${tok}` : ''}{r.createdAt ? ` · ${formatClinicTime(r.createdAt, {}, '')}` : ''}
                </span>
                {r.doctorName && <span className="block text-xs text-gray-400 truncate">{r.doctorName}</span>}
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  // On either view: printing the receipt of a payment on A4 goes back to the list, and the page prints
  // from there.
  const a4Page = printA4 ? <PrintableInvoice invoice={printA4} branding={branding} /> : null;

  if (drawerFromEarlierDay) {
    const openedOn = formatClinicDate(drawer.openedAt, { weekday: 'long', day: 'numeric', month: 'long' }, '');
    return (
      <div className="max-w-2xl">
        <section aria-label="Cash drawer" className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
            <Wallet className="h-7 w-7" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Close the previous day's drawer first</h2>
          <p className="text-sm text-gray-600">
            Your drawer has been open since {openedOn}. Count the cash in it and close it, then open today's drawer
            to start billing.
          </p>
          <p className="urdu-text text-sm leading-loose text-gray-700" dir="rtl">
            پچھلے دن کی دراز ابھی کھلی ہے۔ آج کی بلنگ شروع کرنے سے پہلے اس کی نقد رقم گن کر اسے بند کریں۔
          </p>
          <button
            type="button"
            onClick={() => setClosingDrawer(drawer)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
          >
            <Wallet className="h-4 w-4" aria-hidden="true" /> Count and close
          </button>
        </section>
        {closeDrawerDialog}
        {a4Page}
      </div>
    );
  }

  if (needsDrawer) {
    return (
      <div className="max-w-2xl">
        <section aria-label="Cash drawer" className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
            <Wallet className="h-7 w-7" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Open your cash drawer to start billing</h2>
          <p className="text-sm text-gray-600">
            Every payment taken here goes into your drawer. Open it with the cash you are starting with, and count it
            when your shift ends.
          </p>
          <p className="urdu-text text-sm leading-loose text-gray-700" dir="rtl">
            بلنگ شروع کرنے سے پہلے اپنی دراز کھولیں۔ یہاں وصول ہونے والی ہر نقد رقم آپ کی دراز میں درج ہوگی۔
          </p>
          <button
            type="button"
            onClick={() => askToOpen('counter')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
          >
            <Wallet className="h-4 w-4" aria-hidden="true" /> Open drawer
          </button>
        </section>
        {a4Page}
      </div>
    );
  }

  if (!selection) {
    return <div className="max-w-2xl">{listPane}{a4Page}</div>;
  }

  const statusChip = isPaid
    ? <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">Paid · {bill.invoiceNumber}</span>
    : bill?.id
      ? <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">Saved · {bill.invoiceNumber}</span>
      : <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">Not saved yet</span>;

  // One bill at a time. While a bill is open the list and its search are not offered, so the cashier
  // finishes this patient, or goes back to the list, before starting another.
  return (
    <div className="grid gap-6 items-start lg:grid-cols-[minmax(0,1fr)_22rem]">
      <section aria-label="Bill" className="min-w-0 bg-white rounded-2xl shadow-xl">
        <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <button type="button" onClick={backToList} className="mb-1 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back to list
            </button>
            <h2 className="text-xl font-bold text-gray-900 truncate">{patientName || (billLoading ? 'Loading...' : 'Bill')}</h2>
            <p className="text-sm text-gray-500">
              {[patient.mrn, patient.gender && titleCase(patient.gender), formatPatientAge(patient)]
                .filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="text-right text-sm text-gray-600 space-y-1">
            {bill?.prescription?.prescriptionNumber && <div className="font-mono text-xs">{bill.prescription.prescriptionNumber}</div>}
            {bill?.treatmentCourseId && (
              <div>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                  <Syringe className="h-3.5 w-3.5" aria-hidden="true" /> Today's dose
                </span>
              </div>
            )}
            {(doctor || token) && <div>{[doctor, token && `Token ${token}`].filter(Boolean).join(' · ')}</div>}
            {bill && <div>{statusChip}</div>}
          </div>
        </div>

        {banner && (
          <div
            role="alert"
            className={`mx-5 mt-4 rounded-lg border-l-4 p-3 text-sm ${
              banner.tone === 'warn' ? 'bg-amber-50 border-amber-500 text-amber-900' : 'bg-red-50 border-red-500 text-red-800'
            }`}
          >
            {banner.text}
          </div>
        )}

        {editable && shortfalls.length > 0 && (
          <div className="mx-5 mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <div className="text-sm text-red-800">
                <span className="font-semibold">Not enough stock to dispense this bill.</span>{' '}
                Reduce the quantity or remove the item before taking payment.
                <ul className="mt-1.5 space-y-0.5">
                  {shortfalls.map((sf) => (
                    <li key={sf.medicineId} className="text-xs">
                      <span className="font-semibold">{sf.medicineName}</span>: need {sf.needed}, available {sf.available}{' '}
                      <span className="text-red-600 font-semibold">(short by {sf.shortBy})</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {editable && (
          <div className="px-5 pt-4 flex flex-wrap items-center justify-end gap-2">
            {allowed('APPLY_DISCOUNT') && (
              <InvoiceDiscountModeToggle
                mode={bill.discountMode || 'PERCENT'}
                onChange={changeDiscountMode}
                disabled={editing}
              />
            )}
            <button type="button" onClick={() => setShowAddService(true)} disabled={editing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 font-medium disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add Service
            </button>
            <button type="button" onClick={() => setShowCustomItem(true)} disabled={editing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 font-medium disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Custom Item
            </button>
            {supplies.length > 0 && (
              <button type="button" onClick={() => setShowSyringe(true)} disabled={editing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-medium disabled:opacity-50">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Supplies
              </button>
            )}
          </div>
        )}

        <div className="px-5 py-4 overflow-x-auto">
          {billLoading && !bill ? (
            <div className="space-y-2" aria-hidden="true">
              {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse" />)}
            </div>
          ) : (
            <table className="w-full min-w-[32rem] text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-2 text-left font-semibold">Item</th>
                  <th className="py-2 px-2 text-center font-semibold">Qty</th>
                  <th className="py-2 px-2 text-right font-semibold">Price</th>
                  <th className="py-2 px-2 text-right font-semibold">Discount</th>
                  <th className="py-2 pl-2 text-right font-semibold">Total</th>
                  {editable && <th className="py-2 pl-2 w-10"><span className="sr-only">Remove</span></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, idx) => {
                  const sf = shortByMedicine[item.prescriptionMedicine?.medicine?.id];
                  const disc = Number(item.discountAmount) || 0;
                  return (
                    <tr key={item.id || item.prescriptionMedicine?.id || idx}>
                      <td className="py-2.5 pr-2">
                        <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                          {item.name}
                          {isPaid && item.itemType === 'MEDICINE' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">Dispensed</span>
                          )}
                        </div>
                        {item.description && <div className="text-xs text-gray-500">{item.description}</div>}
                      </td>
                      <td className="py-2.5 px-2 text-center tabular-nums">
                        {editable && sf ? (
                          <ShortfallQuantityInput
                            quantity={item.quantity}
                            available={sf.available}
                            label={item.name}
                            showToast={showToast}
                            onSave={(n) => saveQuantity(item, n)}
                          />
                        ) : item.quantity}
                      </td>
                      <td className="py-2.5 px-2 text-right tabular-nums">{formatAmountCell(item.unitPrice)}</td>
                      <td className="py-2.5 px-2 text-right">
                        {editable && allowed('APPLY_DISCOUNT') ? (
                          <ItemDiscountInput
                            currentDiscount={percentMode ? Number(item.discountPercent || 0) : disc}
                            percentMode={percentMode}
                            label={item.name}
                            onSave={(v) => saveDiscount(item, v)}
                          />
                        ) : (
                          <span className={`tabular-nums ${disc > 0 ? 'text-emerald-700 font-medium' : 'text-gray-400'}`}>
                            {disc > 0 ? `-${formatAmountCell(disc)}` : '—'}
                            {disc > 0 && Number(item.discountPercent) > 0 && (
                              <span className="text-xs text-gray-400 ml-1">({Number(item.discountPercent)}%)</span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pl-2 text-right font-semibold text-gray-900 tabular-nums">{formatAmountCell(item.total)}</td>
                      {editable && (
                        <td className="py-2.5 pl-2 text-right">
                          <button
                            type="button"
                            onClick={() => removeLine(item)}
                            disabled={editing}
                            aria-label={`Remove ${item.name}`}
                            title="Remove"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {items.length === 0 && !billLoading && (
                  <tr><td colSpan={editable ? 6 : 5} className="py-6 text-center text-gray-400">No items on this bill.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {bill && (
          <div className="px-5 pb-5 flex flex-wrap items-end justify-between gap-4">
            <div className="flex items-center gap-2">
              {editable && bill.id && allowed('CANCEL_INVOICE') && (
                <button
                  type="button"
                  onClick={() => setShowDiscard(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  <X className="h-4 w-4" aria-hidden="true" /> Discard bill
                </button>
              )}
            </div>
            <dl className="min-w-[15rem] space-y-1 text-sm tabular-nums">
              <div className="flex justify-between gap-6 text-gray-600"><dt>Items</dt><dd>{formatPaisa(gross)}</dd></div>
              {lineDiscounts > 0 && (
                <div className="flex justify-between gap-6 text-emerald-700"><dt>Line discounts</dt><dd>-{formatPaisa(lineDiscounts)}</dd></div>
              )}
              {toPaisa(bill.discountAmount) > 0 && (
                <div className="flex justify-between gap-6 text-emerald-700"><dt>Additional discount</dt><dd>-{formatAmountCell(bill.discountAmount)}</dd></div>
              )}
              {toPaisa(shownRounding) !== 0 && (
                <div className="flex justify-between gap-6 text-emerald-700">
                  <dt>Round off{payingCash ? ' (cash)' : ''}</dt>
                  <dd>{formatSigned(shownRounding)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-6 border-t border-gray-200 pt-1.5 text-base font-bold text-gray-900"><dt>Total</dt><dd>{formatRs(shownTotal)}</dd></div>
            </dl>
          </div>
        )}
      </section>

      <div className="space-y-4 lg:sticky lg:top-6">
        {paid ? (
          <section aria-label="Payment" className="bg-white rounded-2xl shadow-xl p-6 text-center space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600 text-white">
              <Check className="h-6 w-6" aria-hidden="true" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">
              Paid.
            </h3>
            <p className="text-sm text-gray-600">{paidSummary(paid)}</p>
            <div className="flex flex-wrap justify-center gap-2 pt-1">
              {allowed('PRINT_INVOICE') && (
                <button
                  type="button"
                  onClick={() => setReceipt({ invoice: paid, afterPayment: paidNow })}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  <Printer className="h-4 w-4" aria-hidden="true" /> Print receipt
                </button>
              )}
              <button
                ref={nextPatientRef}
                type="button"
                onClick={backToList}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
              >
                <Receipt className="h-4 w-4" aria-hidden="true" /> Next patient
              </button>
            </div>
          </section>
        ) : bill && bill.status === 'DRAFT' ? (
          <CashTenderPanel
            total={Number(bill.grandTotal) || 0}
            roundingAdjustment={Number(bill.roundingAdjustment) || 0}
            cashTotal={bill.cashGrandTotal != null ? Number(bill.cashGrandTotal) : null}
            cashRoundingAdjustment={
              bill.cashRoundingAdjustment != null ? Number(bill.cashRoundingAdjustment) : null
            }
            value={tender}
            onChange={setTender}
            onCollect={collect}
            canCollect={allowed('FINALIZE_INVOICE')}
            blockedReason={blockedReason}
            busy={busy}
            collectRef={collectRef}
          />
        ) : null}
      </div>

      {a4Page}

      {receipt && (
        <InvoiceReceiptModal
          invoice={receipt.invoice}
          branding={branding}
          paper={settings.paper}
          isReprint={!receipt.afterPayment}
          onClose={closeReceipt}
          onPrintA4={() => setPrintA4(receipt.invoice)}
          onPrintBlocked={() => showToast('error', 'Could not open the print window. Please allow pop-ups for this site.')}
        />
      )}

      {showAddService && (
        <AddServiceModal
          services={services.filter((s) => s.isActive)}
          onAdd={addService}
          onClose={() => setShowAddService(false)}
        />
      )}
      {showCustomItem && (
        <CustomItemModal onAdd={addCustomItem} onClose={() => setShowCustomItem(false)} />
      )}
      {showSyringe && (
        <SupplyPickerModal
          supplies={supplies}
          onAdd={addSupply}
          onClose={() => setShowSyringe(false)} />
      )}
      {showDiscard && bill?.id && (
        <DiscardBillDialog
          invoiceNumber={bill.invoiceNumber}
          saving={discarding}
          onConfirm={discard}
          onClose={() => setShowDiscard(false)}
        />
      )}
    </div>
  );
};

const formatAmountCell = (rupees) => formatPaisa(toPaisa(rupees));

const paidSummary = (inv) => {
  if (!inv) return '';
  if (toPaisa(inv.grandTotal) === 0) return 'Completed with nothing to collect.';
  let text = `${formatRs(inv.grandTotal)} collected by ${PAYMENT_MODE_LABEL[inv.paymentMode] || inv.paymentMode || 'an unrecorded method'}.`;
  if (inv.paymentMode === 'CASH' && inv.cashTendered != null) {
    text += ` Cash received ${formatRs(inv.cashTendered)}, change given ${formatRs(inv.changeReturned || 0)}.`;
  }
  return text;
};

/**
 * Discarding a saved bill cancels it with a reason, and the prescription goes back to waiting. The
 * common reasons are one tap; anything else is written out.
 */
const DiscardBillDialog = ({ invoiceNumber, saving, onConfirm, onClose }) => {
  const [choice, setChoice] = useState(null);
  const [other, setOther] = useState('');
  const [error, setError] = useState('');
  const close = () => { if (!saving) onClose(); };
  const dialogRef = useDialog(close, { isDirty: () => choice !== null || other.trim() !== '' });
  const reason = choice === 'OTHER' ? other.trim() : choice;

  const submit = () => {
    if (!reason) {
      setError(choice === 'OTHER' ? 'Write the reason.' : 'Choose a reason.');
      return;
    }
    onConfirm(reason);
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="discard-bill-title" className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between bg-gradient-to-r from-red-600 to-rose-600">
          <h3 id="discard-bill-title" className="text-lg font-bold text-white">Discard bill {invoiceNumber}</h3>
          <button type="button" aria-label="Close" onClick={close} className="p-2 hover:bg-white/20 rounded-lg"><X className="h-5 w-5 text-white" /></button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-600">
            Nothing has been paid on this bill. It is cancelled with the reason you choose, and the patient goes back to waiting to be billed.
          </p>
          <div role="group" aria-label="Reason" className="grid gap-2">
            {[...DISCARD_REASONS, 'OTHER'].map((r) => (
              <button
                key={r}
                type="button"
                aria-pressed={choice === r}
                onClick={() => { setChoice(r); setError(''); }}
                className={`w-full rounded-xl border px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                  choice === r ? 'border-red-500 bg-red-50 text-red-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {r === 'OTHER' ? 'Other' : r}
              </button>
            ))}
          </div>
          {choice === 'OTHER' && (
            <div>
              <label htmlFor="discard-other-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                id="discard-other-reason"
                rows={3}
                maxLength={500}
                value={other}
                onChange={(e) => { setOther(e.target.value); setError(''); }}
                className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-red-500"
              />
            </div>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
        <div className="px-6 pb-6 flex justify-end gap-3">
          <button type="button" onClick={close} className="px-5 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">Keep bill</button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
          >
            {saving ? 'Discarding...' : 'Discard bill'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Every medical supply the store carries, for the counter to put on the bill. Suggested first (a
 * syringe when the prescription has an injection), then whatever is in stock, then the rest. One
 * that has run out is still offered: the counter may have a box the system has not caught up with,
 * and refusing to bill what was actually used helps nobody and loses the record of it.
 */
const SupplyPickerModal = ({ supplies, onAdd, onClose }) => {
  const dialogRef = useDialog(onClose, { confirmDiscard: false });
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q
    ? supplies.filter((x) => (x.name || '').toLowerCase().includes(q)
        || (x.type || '').toLowerCase().includes(q))
    : supplies;
  const label = (t) => (t ? t.replace(/_/g, ' ').toLowerCase() : '');

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div role="dialog" aria-modal="true" className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex-shrink-0">
          <h2 className="text-xl font-bold text-white">Add a Supply</h2>
          <p className="text-emerald-100 text-sm">Anything used for this patient, prescribed or not</p>
        </div>
        <div className="p-4 pb-2 flex-shrink-0">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search supplies"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <div className="px-4 pb-2 space-y-2 flex-1 overflow-y-auto min-h-0">
          {shown.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-8">Nothing matches that</p>
          ) : shown.map((sy) => (
            <button
              key={sy.id}
              type="button"
              onClick={() => onAdd(sy.id)}
              className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors text-left ${
                sy.suggested ? 'border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50' : 'border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/40'
              }`}
            >
              <span className="min-w-0">
                <span className="block font-medium text-gray-900 truncate">
                  {sy.name}
                  {sy.suggested && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">
                      For the injection
                    </span>
                  )}
                </span>
                <span className={`block text-xs ${sy.inStock ? 'text-gray-500' : 'text-amber-700 font-medium'}`}>
                  {label(sy.type)}{sy.type ? ' · ' : ''}{sy.inStock ? `${sy.quantity} in stock` : 'Out of stock'}
                </span>
              </span>
              <span className="text-sm font-semibold text-gray-700 tabular-nums flex-shrink-0">
                {sy.sellingPrice != null ? `Rs ${sy.sellingPrice}` : ''}
              </span>
            </button>
          ))}
        </div>
        <div className="p-4 pt-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingCounter;
